import {
  Button,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  type ModalProps,
  Table,
  Tbody,
  Td,
  Tfoot,
  Th,
  Thead,
  Tr,
} from "@chakra-ui/react";
import { Trans } from "@lingui/macro";
import { ButtonLink } from "@src/components/ui-kit";
import { Icon } from "@src/components/ui-kit/Icon";
import { currency, formatDate } from "@src/utils/formatters";
import { secondsToHours } from "@src/utils/time";
import { observer } from "mobx-react-lite";
import Link from "next/link";
import type { FC } from "react";
import type { OurWorkBudgetItemExpenseStatsModel } from "../../models";

type Props = Omit<ModalProps, "children"> & {
  expenseStats: OurWorkBudgetItemExpenseStatsModel[];
  addQueryParams: Partial<{
    brandId: string;
    projectId: string;
    budgetItemId: string;
  }>;
};

export const OurWorkItemExpenseModal: FC<Props> = observer(
  function OurWorkItemExpenseModal({
    isOpen,
    onClose,
    expenseStats,
    addQueryParams,
  }) {
    return (
      <Modal isOpen={isOpen} onClose={onClose} trapFocus={false}>
        <ModalOverlay />
        <ModalContent
          minW={{
            base: "unset",
            md: "760px",
          }}
        >
          <ModalHeader>
            <Trans>Expenses</Trans>
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody overflowX="auto" px="0">
            <Table>
              <Thead
                fontWeight="bold"
                bg="gray.50"
                borderBottomWidth="0.3rem"
                borderBottomColor="gray.200"
              >
                <Tr py="3">
                  <Th textTransform="capitalize">
                    <Trans>Expense</Trans>
                  </Th>
                  <Th textTransform="capitalize">
                    <Trans>Reported date</Trans>
                  </Th>
                  <Th textTransform="capitalize">
                    <Trans>Task</Trans>
                  </Th>
                  <Th textTransform="capitalize">
                    <Trans>Position</Trans>
                  </Th>
                  <Th textTransform="capitalize">
                    <Trans>Tracked hours</Trans>
                  </Th>
                  <Th textTransform="capitalize">
                    <Trans>Amount</Trans>
                  </Th>
                </Tr>
              </Thead>
              <Tbody>
                {expenseStats?.map(
                  ({
                    id,
                    expense,
                    report_date,
                    position,
                    amount,
                    tracked_time,
                    task,
                  }) => (
                    <Tr key={id}>
                      <Td>
                        <Button
                          as={Link}
                          href={{
                            pathname: "/expenses/edit",
                            query: { id: expense?.id },
                          }}
                          size="sm"
                          variant="link"
                        >
                          {expense?.expense_no ?? expense?.id}
                        </Button>
                      </Td>
                      <Td>
                        {report_date ? formatDate(new Date(report_date)) : ""}
                      </Td>
                      <Td>{task?.name}</Td>
                      <Td>{position?.title}</Td>
                      <Td>{secondsToHours(tracked_time)}</Td>
                      <Td>{currency.formatByWorkspace(amount)}</Td>
                    </Tr>
                  ),
                )}
              </Tbody>
              <Tfoot bg="gray.50">
                <Tr>
                  <Th />
                  <Th />
                  <Th />
                  <Th />
                  <Th fontWeight="unset" textTransform="capitalize">
                    <Trans>Total:</Trans>
                  </Th>
                  <Th fontSize="unset" fontWeight="unset">
                    {currency.formatByWorkspace(
                      expenseStats?.reduce((prev, { amount }) => {
                        return amount + prev;
                      }, 0),
                    )}
                  </Th>
                </Tr>
              </Tfoot>
            </Table>
          </ModalBody>
          <ModalFooter gap="4">
            <ButtonLink
              h="9"
              variant="link"
              leftIcon={<Icon name="plus" />}
              onClickCapture={(e) => e.stopPropagation()}
              href={{
                pathname: "/expenses/create",
                query: {
                  brandId: addQueryParams?.brandId,
                  projectId: addQueryParams?.projectId,
                  budgetItemId: addQueryParams?.budgetItemId,
                },
              }}
            >
              <Trans>Add new expense</Trans>
            </ButtonLink>
            <Button onClick={onClose}>
              <Trans>Close</Trans>
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    );
  },
);
